/**
 * /support-inbox - the team mailbox (support@orboto.io and its aliases) as the
 * SUP ticket queue (OCP-617). Health of the poll, what is waiting or failed,
 * the daily digest, the newest conversations with their sender class, ticket
 * links and first-response SLA, the monthly SLA report, and "poll now" with an
 * audited reason. Settings live in
 * the Settings page, Support tab. Same routes as the MCP tools
 * ocp_support_inbox_status / ocp_run_support_inbox_poll / ocp_support_sla_report.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

interface PollCounts {
  fetched: number;
  recorded: number;
  filed: number;
  tickets_created: number;
  comments_added: number;
  skipped: number;
  auto_replies: number;
  replies_sent: number;
  errors: number;
}
interface PollRecord {
  at: string;
  trigger: string;
  ok: boolean;
  duration_ms: number;
  error: string | null;
  counts: PollCounts;
}
interface Conversation {
  id: string;
  ticket_key: string | null;
  ticket_url: string | null;
  status: 'pending_ticket' | 'ticket_created' | 'ticket_failed' | 'ticket_gone';
  sender_class: 'known' | 'automated' | 'unknown';
  digest_day: string | null;
  last_ticket_error: string | null;
  subject: string;
  customer_email: string;
  alias: string;
  tenant: string | null;
  tier: string | null;
  sla_hours: number | null;
  first_received_at: string;
  first_response_at: string | null;
  first_response_service_minutes: number | null;
  sla_met: boolean | null;
  sla_due_at: string | null;
  overdue: boolean;
  auto_reply_at: string | null;
  mails_in: number;
  replies_out: number;
}
interface Problem {
  id: string;
  direction: string;
  status: string;
  to: string | null;
  subject: string | null;
  attempts: number;
  last_error: string | null;
  updated_at: string;
}
export interface SupportStatus {
  enabled: boolean;
  configured: boolean;
  missing: string[];
  settings: {
    jmap_url: string | null;
    username: string | null;
    smtp: string | null;
    project_key: string;
    poll_interval_minutes: number;
    auto_reply_enabled: boolean;
    sla_hours: Record<string, number>;
  };
  within_service_hours_now: boolean;
  health: {
    last_poll_at: string | null;
    last_success_at: string | null;
    last_error_at: string | null;
    last_error: string | null;
    consecutive_failures: number;
    alert_open: boolean;
    polls_total: number;
    recent_polls: PollRecord[];
  };
  counts: Record<string, number>;
  digest: {
    service_day: string | null;
    state: 'pending' | 'sent' | 'empty' | null;
    last_sent_at: string | null;
    last_counts: { unknown: number; automated: number } | null;
    last_error: string | null;
    waiting: { unknown: number; automated: number };
  };
  recent: Conversation[];
  problems: Problem[];
}
interface TierBucket {
  tickets: number;
  answered: number;
  median_first_response_service_minutes: number | null;
  median_first_response_minutes: number | null;
  sla_met: number;
  sla_misses: number;
  open_overdue: number;
}
export interface SlaReport {
  month: string;
  sla_hours: Record<string, number>;
  totals: TierBucket;
  by_tier: Record<string, TierBucket>;
  misses: Array<{ conversation_id: string; ticket_key: string | null; tier: string | null; tenant: string | null; received_at: string; first_response_service_minutes: number | null; target_service_minutes: number; state: string }>;
}

const minutes = (m: number | null) => (m == null ? '-' : m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} min`);
const COUNT_LABELS: Record<string, string> = {
  conversations: 'Conversations',
  pending_ticket: 'Waiting for a ticket',
  ticket_failed: 'Ticket refused / gone',
  inbound_waiting: 'Mails not yet filed',
  inbound_failed: 'Mails refused',
  replies_pending: 'Replies queued',
  replies_failed: 'Replies failed',
  replies_unconfirmed: 'Replies unconfirmed',
  sent_copies_missing: 'Sent copies missing',
  blocked_senders: 'Blocked sender mails',
};
const PROBLEM_KEYS = new Set(['ticket_failed', 'inbound_failed', 'replies_failed', 'replies_unconfirmed']);
const CLASS_STYLES: Record<Conversation['sender_class'], string> = {
  known: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  unknown: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  automated: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function AdminSupportInboxPage() {
  const [data, setData] = useState<SupportStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [month, setMonth] = useState(currentMonth());
  const [sla, setSla] = useState<SlaReport | null>(null);
  const [reason, setReason] = useState('');
  const [retryFailed, setRetryFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api<SupportStatus>('/v1/admin/support-inbox/status'));
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiError ? `Could not load (HTTP ${e.status}).` : 'Could not load.');
    }
  }, []);
  const loadSla = useCallback(async (m: string) => {
    try {
      setSla(await api<SlaReport>(`/v1/admin/support-inbox/sla?month=${encodeURIComponent(m)}`));
    } catch {
      setSla(null);
    }
  }, []);
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 30_000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    void loadSla(month);
  }, [loadSla, month]);

  async function pollNow() {
    if (busy || reason.trim().length < 3) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api<{ ok: boolean; error: string | null; counts: PollCounts }>('/v1/admin/support-inbox/poll', {
        method: 'POST',
        body: { reason: reason.trim(), ...(retryFailed ? { retry_failed: true } : {}) },
      });
      setMsg(
        r.ok
          ? `Poll done: ${r.counts.recorded} new mail(s), ${r.counts.tickets_created} ticket(s), ${r.counts.comments_added} comment(s), ${r.counts.replies_sent} repl${r.counts.replies_sent === 1 ? 'y' : 'ies'} sent.`
          : `Poll failed: ${r.error ?? 'unknown error'}`,
      );
      setReason('');
      setRetryFailed(false);
      await load();
      await loadSla(month);
    } catch (e) {
      const b = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
      setMsg(b?.message ?? (e instanceof ApiError ? `Failed (HTTP ${e.status}).` : 'Failed.'));
    } finally {
      setBusy(false);
    }
  }

  const h = data?.health;
  const state = !data ? null : !data.enabled ? 'off' : !data.configured ? 'not configured' : h && h.consecutive_failures > 0 ? 'failing' : 'ok';

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-lg font-semibold">Support inbox</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Every mail to support@orboto.io and its aliases becomes a ticket in the {data?.settings.project_key ?? 'SUP'} project, unless the sender is on
          the blocklist; a follow-up in the same thread becomes a comment. A known customer pushes at once; unknown and automated senders wait for the
          daily digest at the start of the service hours. A <strong>public</strong> comment on such a ticket is mailed to the customer from the address they wrote to; internal
          comments stay in the workspace. Runbook: deploy/README.md, section &quot;Support mailbox&quot;. Settings:{' '}
          <Link to="/settings?tab=support" className="underline" data-testid="support-settings-link">
            Settings, Support tab
          </Link>
          .
        </p>
      </div>
      {err && <p className="text-sm text-red-700 dark:text-red-400">{err}</p>}

      {data && h && (
        <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4" data-testid="support-health">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-medium">Mailbox</h2>
            <span
              className={`rounded px-2 py-0.5 text-xs font-medium ${
                state === 'ok'
                  ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                  : state === 'off'
                    ? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                    : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
              }`}
              data-testid="support-state"
            >
              {state}
            </span>
          </div>
          <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <dt className="text-gray-500">Account</dt>
            <dd>{data.settings.username ?? '-'}</dd>
            <dt className="text-gray-500">Replies via</dt>
            <dd>{data.settings.smtp ?? 'not configured (replies wait)'}</dd>
            <dt className="text-gray-500">Last poll</dt>
            <dd>{h.last_poll_at ? fmtDateTime(h.last_poll_at) : 'never'} (every {data.settings.poll_interval_minutes} min)</dd>
            <dt className="text-gray-500">Last success</dt>
            <dd>{h.last_success_at ? fmtDateTime(h.last_success_at) : 'never'}</dd>
            <dt className="text-gray-500">Service hours now</dt>
            <dd>{data.within_service_hours_now ? 'open' : `closed${data.settings.auto_reply_enabled ? ' (auto-reply on)' : ''}`}</dd>
            <dt className="text-gray-500">Waiting for the digest</dt>
            <dd data-testid="support-digest-waiting">
              {data.digest.waiting.unknown} unknown, {data.digest.waiting.automated} automated
            </dd>
            <dt className="text-gray-500">Last digest</dt>
            <dd data-testid="support-digest-last">
              {data.digest.last_sent_at ? fmtDateTime(data.digest.last_sent_at) : 'never'}
              {data.digest.last_counts ? ` (${data.digest.last_counts.unknown} unknown, ${data.digest.last_counts.automated} automated)` : ''}
            </dd>
            {h.consecutive_failures > 0 && (
              <>
                <dt className="text-gray-500">Failed polls in a row</dt>
                <dd className="text-red-700 dark:text-red-400">
                  {h.consecutive_failures}
                  {h.alert_open ? ' - alert open' : ''}
                </dd>
              </>
            )}
          </dl>
          {data.missing.length > 0 && (
            <p className="mt-2 text-sm text-red-700 dark:text-red-400" data-testid="support-missing">
              Missing: {data.missing.join(', ')}
            </p>
          )}
          {h.last_error && h.consecutive_failures > 0 && <p className="mt-2 break-words text-sm text-red-700 dark:text-red-400">Last error: {h.last_error}</p>}
          {data.digest.state === 'pending' && data.digest.last_error && (
            <p className="mt-2 break-words text-sm text-red-700 dark:text-red-400" data-testid="support-digest-error">
              Digest of {data.digest.service_day} not delivered yet: {data.digest.last_error}
            </p>
          )}

          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="support-counts">
            {Object.entries(data.counts).map(([k, v]) => (
              <div key={k} className={`rounded border px-2 py-1 text-xs ${PROBLEM_KEYS.has(k) && v > 0 ? 'border-red-300 dark:border-red-800' : 'border-gray-200 dark:border-gray-800'}`}>
                <div className="text-gray-500">{COUNT_LABELS[k] ?? k}</div>
                <div className="text-base tabular-nums">{v}</div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              data-testid="support-poll-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for polling now (audited)"
              className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm sm:max-w-md"
            />
            <label className="flex items-center gap-1 text-xs">
              <input type="checkbox" checked={retryFailed} onChange={(e) => setRetryFailed(e.target.checked)} data-testid="support-retry-failed" />
              retry refused
            </label>
            <button
              type="button"
              data-testid="support-poll-now"
              onClick={() => void pollNow()}
              disabled={busy || reason.trim().length < 3 || !data.enabled}
              className="rounded bg-brand-500 px-3 py-1 text-sm font-medium text-white disabled:opacity-50"
            >
              {busy ? 'Polling…' : 'Poll now'}
            </button>
          </div>
          {msg && <p className="mt-2 text-sm" data-testid="support-poll-msg">{msg}</p>}
        </section>
      )}

      {data && data.problems.length > 0 && (
        <section className="rounded-lg border border-red-200 dark:border-red-900 bg-white dark:bg-gray-900 p-4" data-testid="support-problems">
          <h2 className="font-medium">Needs attention</h2>
          <ul className="mt-2 space-y-1 text-sm">
            {data.problems.map((p) => (
              <li key={p.id}>
                <span className="font-medium">{p.direction === 'inbound' ? 'Mail' : p.direction === 'auto_reply' ? 'Auto-reply' : 'Reply'}</span> {p.status}
                {p.to ? ` to ${p.to}` : ''} after {p.attempts} attempt(s): <span className="break-words text-red-700 dark:text-red-400">{p.last_error ?? '-'}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data && (
        <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
          <h2 className="font-medium">Recent conversations</h2>
          {data.recent.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500">No mail filed yet.</p>
          ) : (
            <div className="mt-2 overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="support-recent">
                <thead className="text-left text-xs text-gray-500">
                  <tr>
                    <th className="py-1 pr-3">Ticket</th>
                    <th className="py-1 pr-3">Class</th>
                    <th className="py-1 pr-3">Subject</th>
                    <th className="py-1 pr-3">From</th>
                    <th className="py-1 pr-3">To</th>
                    <th className="py-1 pr-3">Tenant / tier</th>
                    <th className="py-1 pr-3">Received</th>
                    <th className="py-1 pr-3">First response</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.map((c) => (
                    <tr key={c.id} className="border-t border-gray-100 dark:border-gray-800 align-top">
                      <td className="py-1 pr-3 whitespace-nowrap">
                        {c.ticket_url ? (
                          <a href={c.ticket_url} target="_blank" rel="noreferrer" className="underline">
                            {c.ticket_key}
                          </a>
                        ) : null}
                        {c.ticket_url && c.status === 'ticket_gone' ? (
                          <span className="ml-1 text-red-700 dark:text-red-400" title={c.last_ticket_error ?? ''}>
                            (gone)
                          </span>
                        ) : null}
                        {c.ticket_url ? null : (
                          <span className={c.status === 'ticket_failed' ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'} title={c.last_ticket_error ?? ''}>
                            {c.status === 'ticket_failed' ? 'refused' : 'pending'}
                          </span>
                        )}
                      </td>
                      <td className="py-1 pr-3 whitespace-nowrap">
                        <span className={`rounded px-1.5 py-0.5 text-xs ${CLASS_STYLES[c.sender_class]}`} data-testid={`support-class-${c.id}`}>
                          {c.sender_class}
                        </span>
                      </td>
                      <td className="py-1 pr-3 break-words">{c.subject}</td>
                      <td className="py-1 pr-3 break-all">{c.customer_email}</td>
                      <td className="py-1 pr-3 break-all">{c.alias}</td>
                      <td className="py-1 pr-3">{c.tenant ?? '-'}{c.tier ? ` / ${c.tier}` : ''}</td>
                      <td className="py-1 pr-3 whitespace-nowrap">{fmtDateTime(c.first_received_at)}{c.auto_reply_at ? ' (auto-replied)' : ''}</td>
                      <td className="py-1 pr-3 whitespace-nowrap">
                        {c.first_response_at ? (
                          <span className={c.sla_met === false ? 'text-red-700 dark:text-red-400' : ''}>
                            {minutes(c.first_response_service_minutes)}
                            {c.sla_met === false ? ' (SLA missed)' : ''}
                          </span>
                        ) : c.overdue ? (
                          <span className="text-red-700 dark:text-red-400">overdue</span>
                        ) : c.sla_due_at ? (
                          `due ${fmtDateTime(c.sla_due_at)}`
                        ) : (
                          'open'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4" data-testid="support-sla">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-medium">First-response SLA</h2>
          <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm" data-testid="support-sla-month" />
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Terms 3.3, counted in service hours (Mo-Fr 09:00-18:00 Europe/Berlin, without NRW holidays). Targets:{' '}
          {sla ? Object.entries(sla.sla_hours).map(([k, v]) => `${k} ${v} h`).join(', ') : '-'}.
        </p>
        {sla && (
          <div className="mt-2 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-xs text-gray-500">
                <tr>
                  <th className="py-1 pr-3">Tier</th>
                  <th className="py-1 pr-3">Tickets</th>
                  <th className="py-1 pr-3">Answered</th>
                  <th className="py-1 pr-3">Median (service time)</th>
                  <th className="py-1 pr-3">Median (wall clock)</th>
                  <th className="py-1 pr-3">Misses</th>
                  <th className="py-1 pr-3">Open overdue</th>
                </tr>
              </thead>
              <tbody>
                {[...Object.entries(sla.by_tier), ['total', sla.totals] as [string, TierBucket]].map(([tier, b]) => (
                  <tr key={tier} className={`border-t border-gray-100 dark:border-gray-800 ${tier === 'total' ? 'font-medium' : ''}`} data-testid={`sla-row-${tier}`}>
                    <td className="py-1 pr-3">{tier}</td>
                    <td className="py-1 pr-3 tabular-nums">{b.tickets}</td>
                    <td className="py-1 pr-3 tabular-nums">{b.answered}</td>
                    <td className="py-1 pr-3">{minutes(b.median_first_response_service_minutes)}</td>
                    <td className="py-1 pr-3">{minutes(b.median_first_response_minutes)}</td>
                    <td className={`py-1 pr-3 tabular-nums ${b.sla_misses > 0 ? 'text-red-700 dark:text-red-400' : ''}`}>{b.sla_misses}</td>
                    <td className={`py-1 pr-3 tabular-nums ${b.open_overdue > 0 ? 'text-red-700 dark:text-red-400' : ''}`}>{b.open_overdue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sla.misses.length > 0 && (
              <ul className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                {sla.misses.map((m) => (
                  <li key={m.conversation_id}>
                    {m.ticket_key ?? m.conversation_id} ({m.tier ?? 'no tier'}{m.tenant ? `, ${m.tenant}` : ''}): {m.state === 'answered_late' ? `answered after ${minutes(m.first_response_service_minutes)}` : 'still unanswered'}, target {minutes(m.target_service_minutes)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
